import { useCallback, useMemo } from "react"

import { useTransportQuery, useTransportQueryUtils } from "@/transport"

import { getDefaultTagColor } from "../constants/tag-colors"
import type { OrganizationTag, TagSuggestion } from "../types"
import {
  buildOrganizationTagsByName,
  buildTagColorMap,
} from "../utils/tagCatalog"

const LIST_ORGANIZATION_TAGS_OPERATION = "listOrganizationTags"

interface UseOrganizationTagsOptions {
  enabled?: boolean
}

interface UseOrganizationTagsResult {
  tags: OrganizationTag[]
  suggestions: TagSuggestion[]
  tagColorMap: Record<string, string | null>
  tagsByName: Record<string, OrganizationTag>
  isLoading: boolean
  isError: boolean
  error: Error | null
  invalidate: () => Promise<void>
}

export function useOrganizationTags(
  options?: UseOrganizationTagsOptions,
): UseOrganizationTagsResult {
  const transportQueryUtils = useTransportQueryUtils()
  const query = useTransportQuery({
    operation: LIST_ORGANIZATION_TAGS_OPERATION,
    options: {
      enabled: options?.enabled ?? true,
    },
  })

  const tags = useMemo<OrganizationTag[]>(() => {
    if (!query.data) {
      return []
    }
    return query.data.tags.map((tag) => ({
      id: tag.id,
      name: tag.name,
      color: tag.color ?? getDefaultTagColor(tag.name),
    }))
  }, [query.data])

  const suggestions = useMemo<TagSuggestion[]>(() => {
    return tags.map((tag) => ({
      id: tag.id,
      tag: tag.name,
      color: tag.color ?? getDefaultTagColor(tag.name),
    }))
  }, [tags])

  const tagColorMap = useMemo(
    () => buildTagColorMap(suggestions),
    [suggestions],
  )
  const tagsByName = useMemo(() => buildOrganizationTagsByName(tags), [tags])

  const invalidate = useCallback(
    () => transportQueryUtils.invalidate(LIST_ORGANIZATION_TAGS_OPERATION),
    [transportQueryUtils],
  )

  return {
    tags,
    suggestions,
    tagColorMap,
    tagsByName,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    invalidate,
  }
}
